import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Profile.css';

export default function Profile() {
  const { user, token, logout, updateUser } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('profileActiveTab') || 'account';
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [availableExams, setAvailableExams] = useState([]);
  const [examsLoading, setExamsLoading] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const API_AUTH_URL = 'https://api.avtechbmt.cloud/api/v1/auth';
  const API_ENROLL_URL = 'https://api.avtechbmt.cloud/api/v1/user';
  const API_EXAM_URL = 'https://api.avtechbmt.cloud/api/exams';

  useEffect(() => {
    if (activeTab === 'courses' && user) {
      fetchEnrolledCourses();
    } else if (activeTab === 'exams' && user) {
      fetchAvailableExams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchEnrolledCourses = async () => {
    setCoursesLoading(true);
    setError('');
    const authToken = token || localStorage.getItem('token');

    try {
      const res = await fetch(`${API_ENROLL_URL}/my-courses`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || 'Failed to fetch courses');
      setEnrolledCourses(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setCoursesLoading(false);
    }
  };

  const fetchAvailableExams = async () => {
    setExamsLoading(true);
    setError('');
    const authToken = token || localStorage.getItem('token');

    try {
      const res = await fetch(`${API_EXAM_URL}/available`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || 'Failed to fetch exams');
      setAvailableExams(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setExamsLoading(false);
    }
  };

  const promptExamSecurityWarning = (exam) => {
    setSelectedExam(exam);
  };

  const confirmAndStartExam = async () => {
    if (!selectedExam) return;
    const examId = selectedExam._id;
    const authToken = token || localStorage.getItem('token');

    try {
      const res = await fetch(`${API_EXAM_URL}/${examId}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setSelectedExam(null);
        navigate(`/exam/${examId}`, { state: { sessionData: data.data } });
      } else {
        setError(data.message || 'You already submitted Exam or session error');
        setSelectedExam(null);
      }
    } catch (err) {
      setError('Error connecting to backend server');
      setSelectedExam(null);
    }
  };

  const handleUnenroll = async (courseId) => {
    if (!window.confirm('Are you sure you want to unenroll from this course?')) return;

    setActionLoadingId(courseId);
    setError('');
    setSuccess('');
    const authToken = token || localStorage.getItem('token');

    try {
      const res = await fetch(`${API_ENROLL_URL}/courses/${courseId}/unenroll`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || 'Failed to unenroll');

      setSuccess(data.message);
      setEnrolledCourses((prev) => prev.filter((course) => course._id !== courseId));
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoadingId(null);
    }
  };

  if (!user) {
    return (
      <div className="profile-layout" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="metric-card metric-card--tape" style={{ textAlign: 'center', maxWidth: '420px', width: '100%' }}>
          <span className="field-label" style={{ marginBottom: '0.75rem' }}>03 / Overview</span>
          <h2 className="profile-title" style={{ fontSize: '1.6rem' }}>
            Access <em>Denied</em>
          </h2>
          <p className="profile-subtitle" style={{ marginBottom: '1.75rem' }}>
            You must be logged in to view this terminal.
          </p>
          <button className="cta-primary" style={{ width: '100%' }} onClick={() => navigate('/login')}>
            Go to Login →
          </button>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('profileActiveTab');
    logout();
    navigate('/login');
  };

  const handleTabSelect = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('profileActiveTab', tab);
    setSidebarOpen(false);
    setError('');
    setSuccess('');
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const authToken = token || localStorage.getItem('token');
    if (!authToken) {
      setError('Session expired. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      const payload = { name, phone };
      if (newPassword) {
        payload.newPassword = newPassword;
        payload.currentPassword = currentPassword;
      }

      const res = await fetch(`${API_AUTH_URL}/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update profile');

      updateUser({ ...user, name: data.user.name, phone: data.user.phone });
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /* Section number mapping for the editorial eyebrow */
  const sectionNum =
    activeTab === 'account'
      ? '01 / Account'
      : activeTab === 'exams'
      ? '02 / Assessments'
      : activeTab === 'courses'
      ? '03 / Learning'
      : '04 / Preferences';

  return (
    <div className="profile-layout">
      {/* Mobile Sticky Bar */}
      <div className="profile-mobile-header">
        <div>
          <span className="field-label">03 / User Terminal</span>
          <h1 className="profile-brand-text">{user.name || 'Account'}</h1>
        </div>
        <button
          className={`hamburger-btn ${sidebarOpen ? 'open' : ''}`}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle user menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`profile-sidebar ${sidebarOpen ? 'mobile-show' : ''}`}>
        <div className="sidebar-header desktop-only">
          <span className="field-label">03 / User Terminal</span>
          <h1 className="sidebar-brand">{user.name || 'Account Details'}</h1>
        </div>

        <span className="sidebar-section-label">Navigation</span>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => handleTabSelect('account')}
          >
            <span className="nav-icon">👤</span>
            <span className="nav-text">Overview &amp; Security</span>
            <span className="nav-arrow">→</span>
          </button>

          <button
            className={`nav-item ${activeTab === 'exams' ? 'active' : ''}`}
            onClick={() => handleTabSelect('exams')}
          >
            <span className="nav-icon">📝</span>
            <span className="nav-text">Available Exams</span>
            <span className="nav-arrow">→</span>
          </button>

          <button
            className={`nav-item ${activeTab === 'courses' ? 'active' : ''}`}
            onClick={() => handleTabSelect('courses')}
          >
            <span className="nav-icon">📚</span>
            <span className="nav-text">My Enrolled Courses</span>
            <span className="nav-arrow">→</span>
          </button>

          <button
            className={`nav-item ${activeTab === 'preferences' ? 'active' : ''}`}
            onClick={() => handleTabSelect('preferences')}
          >
            <span className="nav-icon">⚙️</span>
            <span className="nav-text">Preferences</span>
            <span className="nav-arrow">→</span>
          </button>

          {user.role === 'admin' && (
            <button className="nav-item nav-item-admin" onClick={() => navigate('/admin')}>
              <span className="nav-icon">🛠️</span>
              <span className="nav-text">Admin Terminal</span>
              <span className="nav-arrow">↗</span>
            </button>
          )}
        </nav>

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="btn-logout">
            Terminate Session
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="profile-main">
        {error && <div className="auth-error" style={{ marginBottom: '1.5rem' }}>{error}</div>}
        {success && <div className="auth-success" style={{ marginBottom: '1.5rem' }}>{success}</div>}

        {/* ================= ACCOUNT TAB ================= */}
        {activeTab === 'account' && (
          <div>
            <div className="profile-section-header">
              <div>
                <h2 className="profile-title">
                  Account <em>Details</em>
                </h2>
                <p className="profile-subtitle">
                  Verified system parameters &amp; security settings
                </p>
              </div>
              <span className="profile-section-num">{sectionNum}</span>
            </div>

            {isEditing ? (
              <div className="metric-card metric-card--tape">
                <form onSubmit={handleUpdateProfile}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: '1.5rem',
                    }}
                  >
                    <div className="form-group">
                      <label className="field-label">Full Name</label>
                      <input
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="John Doe"
                      />
                    </div>

                    <div className="form-group">
                      <label className="field-label">Email Address</label>
                      <input
                        type="email"
                        value={user.email}
                        disabled
                        style={{ opacity: 0.55, cursor: 'not-allowed' }}
                      />
                    </div>

                    <div className="form-group">
                      <label className="field-label">Phone Number</label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+91..."
                      />
                    </div>
                  </div>

                  <hr
                    style={{
                      margin: '2rem 0 1.5rem',
                      border: 'none',
                      borderTop: '1px solid var(--pf-line)',
                    }}
                  />

                  <span className="field-label" style={{ marginBottom: '1rem' }}>
                    Password Security Override
                  </span>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: '1.5rem',
                    }}
                  >
                    <div className="form-group">
                      <label className="field-label">Current Password</label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Current password"
                      />
                    </div>

                    <div className="form-group">
                      <label className="field-label">New Password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="New password"
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem', flexWrap: 'wrap' }}>
                    <button type="submit" disabled={loading} className="cta-primary">
                      {loading ? 'Saving…' : 'Save Changes →'}
                    </button>
                    <button
                      type="button"
                      className="cta-secondary"
                      onClick={() => {
                        setIsEditing(false);
                        setName(user.name);
                        setPhone(user.phone || '');
                        setCurrentPassword('');
                        setNewPassword('');
                        setError('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="metric-card metric-card--tape">
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1.75rem',
                    marginBottom: '2rem',
                  }}
                >
                  <div className="profile-field">
                    <span className="field-label">Full Name</span>
                    <div className="field-value">{user.name || 'N/A'}</div>
                  </div>

                  <div className="profile-field">
                    <span className="field-label">Email Address</span>
                    <div className="field-value">{user.email}</div>
                  </div>

                  <div className="profile-field">
                    <span className="field-label">Phone Number</span>
                    <div className="field-value">{user.phone || 'Not provided'}</div>
                  </div>

                  <div className="profile-field">
                    <span className="field-label">Account Role</span>
                    <div style={{ marginTop: '0.35rem' }}>
                      <span className={`role-badge ${user.role === 'admin' ? 'admin' : 'user'}`}>
                        {user.role || 'User'}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button className="cta-primary" onClick={() => setIsEditing(true)}>
                    Edit Profile Details →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ================= EXAMS TAB ================= */}
        {activeTab === 'exams' && (
          <div>
            <div className="profile-section-header">
              <div>
                <h2 className="profile-title">
                  Available <em>Assessments</em>
                </h2>
                <p className="profile-subtitle">Live exams ready for examination</p>
              </div>
              <span className="profile-section-num">{sectionNum}</span>
            </div>

            {examsLoading ? (
              <div className="metric-card">Loading assessments…</div>
            ) : availableExams.length === 0 ? (
              <div className="metric-card" style={{ textAlign: 'center', padding: '3rem 1.25rem' }}>
                <p className="profile-subtitle" style={{ margin: 0 }}>
                  No active exams available at the moment.
                </p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {availableExams.map((exam) => (
                  <div key={exam._id} className="metric-card exam-card">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 className="exam-card-title">{exam.title}</h3>
                      <p className="exam-card-desc">
                        {exam.description || 'Standard Assessment'}
                      </p>
                      <div className="exam-card-meta">
                        <span>⏱ Duration: {exam.durationInMinutes} min</span>
                        <span>❓ Questions: {exam.questions?.length || 0}</span>
                      </div>
                    </div>
                    <button
                      className="cta-primary"
                      onClick={() => promptExamSecurityWarning(exam)}
                    >
                      Start Exam →
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ================= COURSES TAB ================= */}
        {activeTab === 'courses' && (
          <div>
            <div className="profile-section-header">
              <div>
                <h2 className="profile-title">
                  My Enrolled <em>Courses</em>
                </h2>
                <p className="profile-subtitle">
                  Active learning programs &amp; subscriptions
                </p>
              </div>
              <span className="profile-section-num">{sectionNum}</span>
            </div>

            {coursesLoading ? (
              <div className="metric-card">Loading enrolled courses…</div>
            ) : enrolledCourses.length === 0 ? (
              <div className="metric-card" style={{ textAlign: 'center', padding: '3rem 1.25rem' }}>
                <p className="profile-subtitle" style={{ marginBottom: '1.5rem' }}>
                  You have not enrolled in any courses yet.
                </p>
                <button className="cta-primary" onClick={() => navigate('/courses')}>
                  Explore Courses →
                </button>
              </div>
            ) : (
              <div className="courses-grid">
                {enrolledCourses.map((course) => (
                  <div key={course._id} className="metric-card course-card">
                    <div>
                      <div className="course-card-header">
                        <span className="course-code">{course.code || 'COURSE'}</span>
                        <span className={`role-badge ${course.isActive ? 'user' : 'admin'}`}>
                          {course.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <h3 className="course-title">{course.title}</h3>
                      <p className="course-desc">{course.description}</p>

                      <div className="course-meta">
                        {course.duration && <span>⏱ {course.duration}</span>}
                        {course.instructor && <span>👨‍🏫 {course.instructor}</span>}
                        {course.price !== undefined && <span>💰 ${course.price}</span>}
                      </div>
                    </div>

                    <div className="course-card-footer">
                      <button
                        className="btn-unenroll"
                        disabled={actionLoadingId === course._id}
                        onClick={() => handleUnenroll(course._id)}
                      >
                        {actionLoadingId === course._id ? 'Unenrolling…' : 'Unenroll'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ================= PREFERENCES TAB ================= */}
        {activeTab === 'preferences' && (
          <div>
            <div className="profile-section-header">
              <div>
                <h2 className="profile-title">
                  User <em>Preferences</em>
                </h2>
                <p className="profile-subtitle">Future module integration ready.</p>
              </div>
              <span className="profile-section-num">{sectionNum}</span>
            </div>

            <div className="metric-card metric-card--tape">
              <p className="profile-subtitle" style={{ margin: 0 }}>
                Preference controls will be available in a future release.
              </p>
            </div>
          </div>
        )}
      </main>

      {/* ================= PRE-EXAM SECURITY MODAL ================= */}
      {selectedExam && (
        <div className="profile-modal-overlay" onClick={() => setSelectedExam(null)}>
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <span className="profile-modal-tag">// Proctored Session</span>
            <h2 className="profile-modal-title">
              Assessment <em>Rules.</em>
            </h2>
            <p className="profile-modal-text">
              Before starting <strong>{selectedExam.title}</strong>, please review and accept
              the strict exam policy:
            </p>

            <ul className="profile-modal-list">
              <li><strong>Full Screen</strong> — The assessment runs in full screen mode.</li>
              <li>
                <strong>No Tab Switching</strong> — Leaving the tab registers a violation.
                Exceeding the limit <strong>auto-submits</strong> your test.
              </li>
              <li>
                <strong>No Copy / Paste</strong> — Copying, pasting, right-click, and DevTools
                are strictly disabled.
              </li>
              <li>
                <strong>Single Attempt</strong> — Submitting or exiting terminates your session.
              </li>
            </ul>

            <div className="profile-modal-actions">
              <button className="cta-primary" onClick={confirmAndStartExam}>
                I Agree &amp; Start Test →
              </button>
              <button className="cta-secondary" onClick={() => setSelectedExam(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}